import { apiClient } from '@/api/client';

export type StockTransferStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'IN_TRANSIT'
  | 'RECEIVED'
  | 'REJECTED'
  | 'CANCELLED';

export interface StockTransferInput {
  fromBranchId: string;
  toBranchId: string;
  items: { materialId: string; variantId?: string; quantity: number }[];
  notes?: string;
}

export interface StockTransferLine {
  id: string;
  materialId: string;
  variantId: string;
  materialName: string;
  unit: string | null;
  size: string | null;
  requestedQuantity: number;
  approvedQuantity: number | null;
  sentQuantity: number | null;
  receivedQuantity: number | null;
  /** Weighted cost of the lots sent; null until sent. */
  unitCost: number | null;
  /** In date, on the sending shelf now. */
  available: number;
}

export interface StockTransfer {
  id: string;
  number: number;
  reference: string;
  status: StockTransferStatus;
  notes: string | null;
  createdAt: string;
  fromBranchId: string;
  fromBranchName: string;
  toBranchId: string;
  toBranchName: string;
  createdBy: string | null;
  createdByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  sentAt: string | null;
  sentByName: string | null;
  receivedAt: string | null;
  receivedByName: string | null;
  receiveNote: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  cancelReason: string | null;
  /** Moved during a procedure: received without a request or approval. */
  recordedAtChair: boolean;
  lines: StockTransferLine[];
}

export interface StockTransferAvailability {
  unit: string;
  variants: { id: string; size: string | null; isImplicit: boolean }[];
  /** Every open branch, with in-date units per size id. */
  branches: { id: string; name: string; available: Record<string, number> }[];
}

export type StockTransferPendingCounts = Record<
  'REQUESTED' | 'APPROVED' | 'IN_TRANSIT',
  number
>;

const unwrap = <T>(response: { data: unknown }): T =>
  ((response.data as { data?: T })?.data ?? response.data) as T;

/**
 * Stock moved between branches: requested, approved by the sending branch,
 * sent (in transit), then received. Nothing moves before it is sent.
 */
export const stockTransfersApi = {
  async create(input: StockTransferInput): Promise<StockTransfer> {
    return unwrap(await apiClient.post('/stock-transfers', input));
  },

  async list(params?: {
    branchId?: string;
    status?: StockTransferStatus;
  }): Promise<StockTransfer[]> {
    return unwrap(await apiClient.get('/stock-transfers', { params }));
  },

  async get(id: string): Promise<StockTransfer> {
    return unwrap(await apiClient.get(`/stock-transfers/${id}`));
  },

  async availability(materialId: string): Promise<StockTransferAvailability> {
    return unwrap(
      await apiClient.get('/stock-transfers/availability', {
        params: { materialId },
      }),
    );
  },

  async pendingCounts(): Promise<StockTransferPendingCounts> {
    return unwrap(await apiClient.get('/stock-transfers/pending-counts'));
  },

  async approve(
    id: string,
    body: { lines?: { lineId: string; quantity: number }[]; note?: string },
  ): Promise<StockTransfer> {
    return unwrap(await apiClient.post(`/stock-transfers/${id}/approve`, body));
  },

  async reject(id: string, reason: string): Promise<StockTransfer> {
    return unwrap(
      await apiClient.post(`/stock-transfers/${id}/reject`, { reason }),
    );
  },

  async send(id: string): Promise<StockTransfer> {
    return unwrap(await apiClient.post(`/stock-transfers/${id}/send`, {}));
  },

  async receive(
    id: string,
    body: {
      lines?: { lineId: string; receivedQuantity: number }[];
      note?: string;
    },
  ): Promise<StockTransfer> {
    return unwrap(await apiClient.post(`/stock-transfers/${id}/receive`, body));
  },

  async cancel(id: string, reason?: string): Promise<StockTransfer> {
    return unwrap(
      await apiClient.post(`/stock-transfers/${id}/cancel`, { reason }),
    );
  },
};
